/* IMPORT AND EXPORT, WHOLE (owner call 2026-09-19: "an import and export
   feature in the settings panel — export as JSON or as MD files; import as
   MD or JSON"). JSON is the lossless form: every field the wire knows.
   Markdown is the portable one: a file per word, the facts as frontmatter,
   the word as the heading, the definition as prose — the same shape the
   page's own Export… writes, so a file this wrote is a file this reads. */
import { buildMarkdown, htmlToMd, fileName, download } from './export.js'

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

/* ── OUT ─────────────────────────────────────────────────────────────────── */
/* a word with no name yet is a page, not a word — it does not travel */
const named = words => words.filter(w => String(w.name || '').trim())
export function wordsToJson(words) {
  const out = named(words).map(({ _page, _local, ...w }) => w)
  return JSON.stringify({ app: 'dictionary.oS', exported: new Date().toISOString(), words: out }, null, 2)
}
export function downloadJson(words) {
  download(fileName(`dictionary-${new Date().toISOString().slice(0, 10)}`, 'json'), wordsToJson(words), 'application/json;charset=utf-8')
}
/* one .md per word. Browsers gate a burst of downloads behind one "allow
   multiple files" prompt; a beat between them keeps every file landing. */
/* THE REFERENCES RIDE ALONG: every file's prose is written with
   `[title|phrase]` against the committed words (export.js linkTerms), so a
   folder of these reads as a vault — each word a page, each use a link. */
// a word's aliases ride with it (its own forms); a synonym is a word of its own, and a term of its own
export const committedTerms = words => named(words).filter(w => w.status !== 'proposed' && w.stance !== 'avoided').map(w => ({ id: w.id, term: w.name, aliases: w.aliases || [] }))
export async function downloadMarkdownFiles(words, { meta = true, terms = committedTerms(words) } = {}) {
  for (const w of named(words)) {
    download(fileName(w.name || 'untitled', 'md'), buildMarkdown(w, { meta, terms }), 'text/markdown;charset=utf-8')
    await new Promise(r => setTimeout(r, 250))
  }
}

/* ── IN ──────────────────────────────────────────────────────────────────── */
/* the smallest Markdown that reads what buildMarkdown writes: paragraphs,
   **bold**, _em_, `code`, [links](url), - lists. Anything else is prose. */
function inline(s) {
  return esc(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^\w])_([^_]+)_(?!\w)/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2">$1</a>')
}
export function mdToHtml(md) {
  const blocks = String(md || '').replace(/\r\n?/g, '\n').trim().split(/\n\s*\n/)
  return blocks.map(b => {
    const lines = b.split('\n')
    if (lines.every(l => /^\s*[-*] /.test(l))) return `<ul>${lines.map(l => `<li>${inline(l.replace(/^\s*[-*] /, ''))}</li>`).join('')}</ul>`
    if (lines.every(l => /^\s*\d+\. /.test(l))) return `<ol>${lines.map(l => `<li>${inline(l.replace(/^\s*\d+\. /, ''))}</li>`).join('')}</ol>`
    const h = /^(#{2,6}) (.*)$/.exec(b)
    if (h) return `<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`
    return `<p>${inline(lines.join(' '))}</p>`
  }).join('')
}
const list = v => Array.isArray(v) ? v.map(String) : String(v || '').replace(/^\[|\]$/g, '').split(',').map(s => s.trim()).filter(Boolean)
const terms = v => (Array.isArray(v) ? v : list(v)).map(t => String((t && typeof t === 'object' ? t.term : t) || '').trim()).filter(Boolean)

/* a .md file → the fields api.create takes. The name comes from the
   frontmatter's `word:` (or `title:`), else the first `# heading`, else the
   file's own name. */
/* THE REFERENCES, READ BACK: `[title|phrase]` becomes the phrase and
   `[title]` the title — plain words again, which the editor re-marks against
   the dictionary as it always does. Only single-bracket references: a `[[note]]`,
   a `[text](url)` or `[text][ref]` link, a `[^1]` footnote and a `[ ]` task
   box are left exactly as written. The pairs are returned as `refs` so a
   host can learn an alias from them if it wants to. */
export function unlinkTerms(md) {
  const refs = []
  const out = String(md || '').replace(/(^|[^\[\]])\[([^\[\]|^][^\]|]*?)(?:\|([^\]]+))?\](?![\(\[])/g, (m, pre, title, phrase) => {
    const t = title.trim()
    if (!t || /^[ xX]$/.test(title)) return m
    refs.push({ title: t, phrase: (phrase || t).trim() })
    return pre + (phrase || title)
  })
  return { text: out, refs }
}
export function parseMarkdownWord(md, filename = '') {
  let src = String(md || '').replace(/\r\n?/g, '\n')
  const fm = {}
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(src)
  if (m) {
    for (const line of m[1].split('\n')) { const kv = /^([\w-]+):\s*(.*)$/.exec(line); if (kv) fm[kv[1].toLowerCase()] = kv[2].trim() }
    src = src.slice(m[0].length)
  }
  let name = fm.word || fm.title || ''
  const h1 = /^\s*# (.+)$/m.exec(src)
  if (h1) { if (!name) name = h1[1].trim(); src = src.replace(h1[0], '') }
  if (!name) name = filename.replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]+/g, ' ').trim() || 'untitled'
  const { text: plain, refs } = unlinkTerms(src)
  const body = mdToHtml(plain)
  const text = htmlToMd(body).replace(/[*_`#>-]/g, '').replace(/\s+/g, ' ').trim()
  const status = /^(committed|proposed)$/.test(fm.status || '') ? fm.status : 'committed'
  const stance = fm.stance === 'avoided' ? 'avoided' : 'held'
  return { name, body, text, tags: list(fm.tags), aliases: list(fm.aliases), synonyms: list(fm.synonyms), antonyms: list(fm.antonyms), status, stance, rather: stance === 'avoided' ? list(fm.rather).map(term => ({ term, id: null })) : [], refs }
}

/* a .json file → words. Accepts this app's own export ({ words: [...] }), a
   bare array, or a single word object. */
export function parseJsonWords(json) {
  const data = typeof json === 'string' ? JSON.parse(json) : json
  const arr = Array.isArray(data) ? data : Array.isArray(data?.words) ? data.words : data && typeof data === 'object' ? [data] : []
  return arr.filter(w => w && typeof w === 'object' && String(w.name || '').trim()).map(w => ({
    name: String(w.name).trim(),
    body: w.body || (w.text ? `<p>${esc(w.text)}</p>` : ''),
    text: w.text || htmlToMd(w.body || ''),
    // links travel by name (a word's id is its graph's), and are linked again on the way in
    tags: list(w.tags), aliases: list(w.aliases), synonyms: terms(w.synonyms), antonyms: terms(w.antonyms),
    status: /^(committed|proposed)$/.test(w.status || '') ? w.status : 'committed',
    stance: w.stance === 'avoided' ? 'avoided' : 'held',
    rather: Array.isArray(w.rather) ? w.rather.map(r => ({ term: String(typeof r === 'string' ? r : r?.term || '').trim(), id: null })).filter(r => r.term) : [],
  }))
}

/* read a File (browser) as text */
export const readText = file => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result || '')); r.onerror = () => rej(r.error); r.readAsText(file) })
